import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';

const TermsAndConditions = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      id: 1,
      title: "1. Eligibility",
      content: "You must be at least 18 years old to use this application and place orders. By using our services, you confirm that you are legally eligible to enter into a binding contract."
    },
    {
      id: 2,
      title: "2. Account Registration",
      content: "To access certain features, you may be required to create an account. You are responsible for maintaining the confidentiality of your login credentials and all activities under your account."
    },
    {
      id: 3,
      title: "3. Products & Services",
      content: "We strive to display accurate product information, pricing, and availability.\n\nProduct images are for representation only; actual products may vary slightly.\n\nWe reserve the right to modify or discontinue products without prior notice."
    },
    {
      id: 4,
      title: "4. Orders & Payments",
      content: "Orders placed through the app/website are subject to acceptance and availability.\n\nPrices are subject to change without notice.\n\nPayments must be made using the available payment methods.\n\nWe reserve the right to cancel any order due to pricing errors, stock issues, or suspected fraudulent activity."
    },
    {
      id: 5,
      title: "5. Delivery & Returns",
      content: "Delivery timelines are estimates and may vary due to external factors.\n\nReturn and refund policies are governed by our Return Policy page.\n\nDamaged or incorrect items must be reported within the specified time."
    },
    {
      id: 6,
      title: "6. User Conduct",
      content: "You agree not to:\n- Use the app for any unlawful purpose\n- Upload or share harmful, abusive, or misleading content\n- Attempt to hack, disrupt, or misuse the platform\n- Impersonate another person or entity\n\nViolation of these rules may result in account suspension or termination."
    },
    {
      id: 7,
      title: "7. Privacy Policy",
      content: "Your use of the app is also governed by our Privacy Policy, which explains how we collect, use, and protect your personal data. By using our services, you consent to such data practices."
    },
    {
      id: 8,
      title: "8. Intellectual Property",
      content: "All content, logos, designs, text, graphics, and software on this platform are the property of Glam Company. You may not copy, reproduce, or distribute any content without written permission."
    },
    {
      id: 9,
      title: "9. Third-Party Services",
      content: "Our app may contain links or integrations with third-party services. We are not responsible for the content, policies, or practices of such third parties."
    },
    {
      id: 10,
      title: "10. Limitation of Liability",
      content: "We are not liable for any indirect, incidental, or consequential damages arising from your use of the app or website. Use the service at your own risk."
    },
    {
      id: 11,
      title: "11. Termination",
      content: "We reserve the right to suspend or terminate your access to the platform without prior notice if you violate these Terms."
    },
    {
      id: 12,
      title: "12. Changes to Terms",
      content: "We may update these Terms & Conditions from time to time. Continued use of the app after changes means you accept the updated terms."
    },
    {
      id: 13,
      title: "13. Governing Law",
      content: "These Terms shall be governed by and interpreted in accordance with the laws of India."
    }
  ];

  return (
    <Box sx={{ bgcolor: '#fbfbfb', minHeight: '100vh', pb: 8 }}>
      {/* Header Section with Gradient */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #000000 0%, #333333 100%)',
          color: 'white',
          py: { xs: 6, md: 10 },
          mb: 6,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              mb: 2,
              letterSpacing: '-0.02em',
              fontFamily: "'Playfair Display', serif"
            }}
          >
            Terms & Conditions
          </Typography>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              display: 'flex',
              justifyContent: 'center',
              '& .MuiBreadcrumbs-li': {
                fontSize: '0.9rem',
                fontWeight: 500
              }
            }}
          >
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate('/')}
              sx={{ cursor: 'pointer', '&:hover': { color: 'white' } }}
            >
              Home
            </Link>
            <Typography color="white">Terms & Conditions</Typography>
          </Breadcrumbs>
        </Container>
      </Box>

      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 4,
            border: '1px solid #eee',
            boxShadow: '0 10px 40px rgba(0,0,0,0.02)',
            background: '#ffffff'
          }}
        >
          <Typography
            variant="body1"
            sx={{
              mb: 4,
              lineHeight: 1.8,
              color: '#444',
              fontSize: '1.1rem'
            }}
          >
            Welcome to <strong>Glam Company</strong>. These Terms & Conditions govern your access and use of our website{' '}
            <Link href="https://glamcompany.in" target="_blank" sx={{ color: '#000', fontWeight: 600 }}>
              glamcompany.in
            </Link>{' '}
            and mobile application operated by Glam Company. By accessing or using our services, you agree to be bound by these Terms. If you do not agree, please do not use our platform.
          </Typography>

          <Divider sx={{ mb: 6 }} />

          {sections.map((section) => (
            <Box key={section.id} sx={{ mb: 5 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: '#000',
                  fontSize: '1.25rem',
                  letterSpacing: '0.01em'
                }}
              >
                {section.title}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-line',
                  color: '#555',
                  lineHeight: 1.7,
                  fontSize: '1rem'
                }}
              >
                {section.content}
              </Typography>
            </Box>
          ))}

          <Divider sx={{ my: 6 }} />

          <Box
            sx={{
              p: 4,
              bgcolor: '#f9f9f9',
              borderRadius: 3,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#000' }}>
              14. Contact Us
            </Typography>
            <Typography variant="body1" sx={{ color: '#555', mb: 1 }}>
              For any questions or concerns regarding these Terms, please contact us at:
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#000',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              📧 tvfmsynergy@gmail.com
            </Typography>
          </Box>
        </Paper>

        {/* <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Last updated: February 16, 2026
          </Typography>
        </Box> */}
      </Container>
    </Box>
  );
};

export default TermsAndConditions;